import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { defer, EMPTY, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import * as L from 'leaflet';
import * as moment from 'moment';
import { ApiService } from '../../../../../services/api.service';
import { AuthService } from '../../../../../services/auth.service';
import { DataService } from '../../../../../services/data.service';
import { TleHelperService } from '../../../../../services/tle-helper.service';
import { TleLocationCatalogService } from './tle-location-catalog.service';
import { filterTleLocations, LocationColorPalette, TleParentLocation, TlePassStatus, TleSubLocation } from './tle-location.model';

type SatelliteTrackEntry = {
  point: L.LatLngTuple;
  timeStamp?: string;
};

const TLE_PREVIEW_SWATH_BUFFER_KM = 200;

type TlePreviewSensorPayloadEntry = {
  timestamp: string;
  noradId: string;
  sensorIds: Array<string | number>;
  name: string;
  operatorKey: string;
};

type LocationFetchContext = {
  readonly location: Readonly<TleSubLocation>;
  readonly locationName: string;
  readonly userId: any;
  readonly batchId: number;
  readonly subscription: Subscription;
};

@Component({
  selector: 'app-tle-preview',
  templateUrl: './tle-preview.component.html',
  styleUrls: ['./tle-preview.component.scss'],
})
export class TlePreviewComponent implements OnInit, AfterViewInit, OnDestroy {
  parents: TleParentLocation[] = [];
  visibleParents: TleParentLocation[] = [];
  selectedLocations: TleSubLocation[] = [];
  selectedIds = new Set<string>();
  expandedIds = new Set<string>();
  activeLocationId: string | null = null;
  searchQuery = '';
  locationsLoading = true;
  locationsError = '';
  legendCollapsed = false;
  showPreviewNotice = true;
  fetchTotal = 0;
  fetchCompleted = 0;
  passStatus = new Map<string, TlePassStatus>();
  private readonly locationColors = new LocationColorPalette();
  private readonly locationMarkers = new Map<string, L.CircleMarker>();
  private readonly locationPassLayers = new Map<string, L.LayerGroup>();
  private readonly subscriptions = new Subscription();
  private readonly locationFetches = new Map<string, LocationFetchContext>();
  private fetchBatchId = 0;
  private destroyed = false;
  @ViewChild('map', { static: false }) mapElementRef?: ElementRef<HTMLElement>;
  @ViewChild('legend', { static: false }) set legendElementRef(ref: ElementRef<HTMLElement> | undefined) {
    if (ref) this.bindLegendMapPropagation(ref.nativeElement);
  }
  private readonly tleHelperService = new TleHelperService();
  tooltipImagePath = '../../../assets/images/union.svg';
  darkTheme = false;

  isFetchingNearestPasses = false;
  mapFontColor = 'white';
  latLngDMS = '';
  zoomLevel = 10;
  mapDistance = 0;
  private currentUser: any;

  private map?: L.Map;
  private satRenderer?: L.Renderer;
  private readonly defaultView: L.LatLngTuple = [1.35, 103.94];
  private resizeObserver?: ResizeObserver;
  private tleLocationsSubscription?: Subscription;
  private satellitePaneSyncBound = false;
  private legendPropagationElement?: HTMLElement;
  private readonly handleWindowResize = (): void => {
    this.scheduleMapResize();
  };

  constructor(
    private readonly authService: AuthService,
    private readonly apiService: ApiService,
    private readonly localStorageService: DataService,
    private readonly locationCatalog: TleLocationCatalogService,
    private readonly zone: NgZone
  ) {
    this.currentUser = this.authService.userValue;
    this.subscriptions.add(this.authService.user.subscribe((user) => {
      this.currentUser = user;
    }));

    this.subscriptions.add(this.localStorageService.darkTheme.subscribe((newValue) => {
      this.darkTheme = newValue;
      this.tooltipImagePath = newValue
        ? '../../../assets/images/union-dark.svg'
        : '../../../assets/images/union.svg';
    }));
  }

  ngOnInit(): void {
    this.loadTleLocations();
    void this.refreshCurrentUser().catch(() => {});
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.handleWindowResize);

    setTimeout(() => {
      if (!this.destroyed) this.initializeMap();
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
    window.removeEventListener('resize', this.handleWindowResize);
    this.resizeObserver?.disconnect();
    this.tleLocationsSubscription?.unsubscribe();
    this.cancelSatelliteDataFetch();
    this.unbindSatellitePaneSync();

    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }

  getLocationColor(id: string): string {
    return this.locationColors.getLocationColor(id);
  }

  trackById(_index: number, item: { id: string }): string { return item.id; }

  searchLocations(value: string): void {
    this.searchQuery = value;
    this.visibleParents = filterTleLocations(this.parents, value);
  }

  isExpanded(parent: TleParentLocation): boolean {
    return !!this.searchQuery.trim() || this.expandedIds.has(parent.id);
  }

  toggleExpanded(parent: TleParentLocation): void {
    if (this.expandedIds.has(parent.id)) this.expandedIds.delete(parent.id);
    else this.expandedIds.add(parent.id);
  }

  private allChildren(parent: TleParentLocation): TleSubLocation[] {
    return this.parents.find(item => item.id === parent.id)?.subLocations ?? [];
  }

  childCount(parent: TleParentLocation): number { return this.allChildren(parent).length; }

  parentChecked(parent: TleParentLocation): boolean {
    const children = this.allChildren(parent);
    return children.length > 0 && children.every(child => this.selectedIds.has(child.id));
  }

  parentIndeterminate(parent: TleParentLocation): boolean {
    return !this.parentChecked(parent) && this.allChildren(parent).some(child => this.selectedIds.has(child.id));
  }

  toggleParent(parent: TleParentLocation, checked: boolean): void {
    if (checked && this.isFetchingNearestPasses) return;
    this.allChildren(parent).forEach(location => this.setLocationSelected(location, checked));
    this.refreshSelectedLocations();
    if (checked) this.fitSelectedLocations();
  }

  toggleLocation(location: TleSubLocation, checked: boolean): void {
    if (checked && this.isFetchingNearestPasses) return;
    this.setLocationSelected(location, checked);
    this.refreshSelectedLocations();
    if (checked) this.focusLocation(location);
  }

  private setLocationSelected(location: TleSubLocation, checked: boolean): void {
    if (checked) {
      if (this.selectedIds.has(location.id)) return;
      this.selectedIds.add(location.id);
      this.addLocationMarker(location);
    } else {
      this.selectedIds.delete(location.id);
      this.locationMarkers.get(location.id)?.remove();
      this.locationMarkers.delete(location.id);
      this.removeLocationPasses(location.id);
      this.passStatus.delete(location.id);
      const context = this.locationFetches.get(location.id);
      if (context) this.finishLocationFetch(context);
      if (this.activeLocationId === location.id) this.activeLocationId = null;
    }
  }

  private refreshSelectedLocations(): void {
    this.selectedLocations = this.parents.flatMap(parent => parent.subLocations)
      .filter(location => this.selectedIds.has(location.id));
    this.scheduleMapResize();
  }

  clearSelectedLocations(): void {
    this.cancelSatelliteDataFetch();
    [...this.selectedLocations].forEach(location => this.setLocationSelected(location, false));
    this.refreshSelectedLocations();
  }

  focusLocation(location: TleSubLocation): void {
    // Only checked locations exist on the map, so an unchecked name click stays inert.
    if (!this.selectedIds.has(location.id)) {
      return;
    }

    this.activeLocationId = location.id;
    this.expandedIds.add(location.parentId);
    this.map?.setView([location.latitude, location.longitude], Math.max(this.map.getZoom(), 12));
    this.locationMarkers.forEach((marker, id) => marker.setStyle({ weight: id === location.id ? 4 : 2 }));
    this.locationMarkers.get(location.id)?.openPopup();
  }

  private addLocationMarker(location: TleSubLocation): void {
    if (!this.map || this.locationMarkers.has(location.id)) return;
    const color = this.getLocationColor(location.id);
    const marker = L.circleMarker([location.latitude, location.longitude], {
      radius: 7, color: '#fff', weight: 2, fillColor: color, fillOpacity: 1,
      pane: 'tleLocationPane', className: 'tle-location-marker',
    });
    const popup = document.createElement('div');
    popup.className = 'tle-location-popup';
    const title = document.createElement('strong');
    title.textContent = location.name;
    const parent = document.createElement('div');
    parent.textContent = this.parents.find(item => item.id === location.parentId)?.name ?? '';
    const coordinates = document.createElement('small');
    coordinates.textContent = this.formatCoordinates(location);
    popup.append(title, parent, coordinates);
    marker.bindPopup(popup);
    marker.on('click', () => this.zone.run(() => this.focusLocation(location)));
    marker.addTo(this.map);
    this.locationMarkers.set(location.id, marker);
  }

  formatCoordinates(location: TleSubLocation): string {
    return `${Math.abs(location.latitude).toFixed(4)}° ${location.latitude < 0 ? 'S' : 'N'}, ${Math.abs(location.longitude).toFixed(4)}° ${location.longitude < 0 ? 'W' : 'E'}`;
  }

  fitSelectedLocations(): void {
    if (!this.map || !this.selectedLocations.length) return;
    this.map.fitBounds(L.latLngBounds(this.selectedLocations.map(location =>
      L.latLng(location.latitude, location.longitude))), { padding: [65, 65], maxZoom: 13 });
  }

  statusLabel(location: TleSubLocation): string {
    const labels: Record<TlePassStatus, string> = {
      idle: 'Ready to fetch', queued: 'Queued', loading: 'Fetching passes…',
      ready: 'Passes loaded', empty: 'No nearby passes', error: 'Could not load passes. Try again.',
    };
    return labels[this.passStatus.get(location.id) ?? 'idle'];
  }

  async toggleFullscreen(): Promise<void> {
    const wrapper = this.mapElementRef?.nativeElement.closest('.tle-preview-wrapper') as HTMLElement;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await wrapper?.requestFullscreen();
      this.scheduleMapResize();
    } catch { /* Browser may not support fullscreen in an embedded preview. */ }
  }

  fetchNearestPasses(): void {
    if (!this.selectedLocations.length || this.isFetchingNearestPasses || this.destroyed) return;
    this.cancelSatelliteDataFetch();
    const contexts: LocationFetchContext[] = this.selectedLocations.map(location => ({
      location: { ...location },
      locationName: this.parents.find(parent => parent.id === location.parentId)?.name ?? '',
      userId: this.currentUser?.id,
      batchId: this.fetchBatchId,
      subscription: new Subscription(),
    }));
    this.fetchTotal = contexts.length;
    this.fetchCompleted = 0;
    this.isFetchingNearestPasses = true;
    // Register the entire batch before subscribing: cached/test responses may be synchronous.
    contexts.forEach(context => {
      this.locationFetches.set(context.location.id, context);
      this.removeLocationPasses(context.location.id);
      this.passStatus.set(context.location.id, 'loading');
    });
    contexts.forEach(context => this.startLocationFetch(context));
  }

  cancelSatelliteDataFetch(): void {
    const contexts = [...this.locationFetches.values()];
    // Invalidate before unsubscribe so old callbacks cannot affect a later batch.
    this.fetchBatchId++;
    this.locationFetches.clear();
    this.isFetchingNearestPasses = false;
    contexts.forEach(context => {
      context.subscription.unsubscribe();
      if (this.selectedIds.has(context.location.id)) {
        this.passStatus.set(context.location.id,
          this.locationPassLayers.has(context.location.id) ? 'ready' : 'idle');
      }
    });
  }

  private startLocationFetch(context: LocationFetchContext): void {
    if (!this.isSatelliteFetchActive(context)) return;
    const request = defer(() => this.apiService.getSatelliteData(
      this.buildSatelliteRequestPayload(context, 'currentTLEPosition')
    )).pipe(
      take(1),
      switchMap((response: any) => {
        if (!this.isSatelliteFetchActive(context) || response?.msg === 'Failed') return EMPTY;
        const satellites = this.filterSatellitesByCurrentPosition(
          this.extractSatelliteResults(response), context.location
        );
        if (!satellites.length) return EMPTY;
        return this.apiService.getSatelliteData(
          this.buildSatelliteRequestPayload(context, 'tlePreview', satellites)
        ).pipe(take(1));
      }),
      map((response: any): TlePassStatus => {
        if (!this.isSatelliteFetchActive(context)) return 'empty';
        return response?.msg !== 'Failed'
          && this.plotNearestPass(this.extractSatelliteResults(response), context.location)
          ? 'ready' : 'empty';
      })
    ).subscribe({
      next: status => this.finishLocationFetch(context, status),
      error: () => this.finishLocationFetch(context, 'error'),
      complete: () => this.finishLocationFetch(context, 'empty'),
    });
    // A synchronously settled context already has a closed subscription container.
    context.subscription.add(request);
  }

  private finishLocationFetch(context: LocationFetchContext, status?: TlePassStatus): void {
    if (context.batchId !== this.fetchBatchId
      || this.locationFetches.get(context.location.id) !== context) return;
    this.locationFetches.delete(context.location.id);
    context.subscription.unsubscribe();
    if (status && this.selectedIds.has(context.location.id)) {
      this.passStatus.set(context.location.id, status);
    }
    this.fetchCompleted++;
    this.isFetchingNearestPasses = this.locationFetches.size > 0;
  }

  private isSatelliteFetchActive(context: LocationFetchContext): boolean {
    return !this.destroyed && context.batchId === this.fetchBatchId
      && this.locationFetches.get(context.location.id) === context
      && this.selectedIds.has(context.location.id);
  }

  private initializeMap(): void {
    const mapElement = this.mapElementRef?.nativeElement;
    if (!mapElement || this.map) {
      return;
    }

    L.Icon.Default.mergeOptions({
      iconUrl: 'assets/map-icons/marker-icon.png',
      iconRetinaUrl: 'assets/map-icons/marker-icon.png',
      shadowUrl: 'assets/map-icons/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
      shadowAnchor: [12, 41],
    });

    const openStreetMap = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    });

    this.map = L.map(mapElement, {
      center: this.defaultView,
      worldCopyJump: true,
      maxBounds: [
        [-90, -180],
        [90, 180],
      ],
      maxBoundsViscosity: 1.0,
      zoom: this.zoomLevel,
      zoomControl: false,
      attributionControl: true,
      minZoom: 1,
      maxZoom: 18,
    });

    openStreetMap.addTo(this.map);
    this.mapFontColor = 'black';
    this.map.attributionControl.setPosition('bottomleft');

    new L.Control.Zoom({ position: 'bottomright' }).addTo(this.map);
    this.addFullscreenControl();
    this.map.createPane('tleLocationPane').style.zIndex = '680';
    this.selectedLocations.forEach(location => this.addLocationMarker(location));

    this.updateMapStats();
    this.observeMapContainer(mapElement);
    this.scheduleMapResize();

    this.map.on('moveend zoomend', () => this.updateMapStats());
    this.map.on('mousemove', (event: L.LeafletMouseEvent) => {
      this.latLngDMS = this.decimalToDMS(event.latlng.lat, event.latlng.lng);
    });

  }

  // A native Leaflet control so it stacks with the zoom control (same corner, spacing, order).
  private addFullscreenControl(): void {
    if (!this.map) {
      return;
    }

    const FullscreenControl = L.Control.extend({
      options: { position: 'bottomright' },
      onAdd: (): HTMLElement => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control tle-fullscreen-control');
        const button = L.DomUtil.create('a', '', container) as HTMLAnchorElement;
        button.href = '#';
        button.title = 'Toggle fullscreen';
        button.setAttribute('role', 'button');
        button.setAttribute('aria-label', 'Toggle fullscreen');
        button.innerHTML = '<span class="material-icons">fullscreen</span>';

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);
        L.DomEvent.on(button, 'click', (event: Event) => {
          L.DomEvent.preventDefault(event);
          this.zone.run(() => this.toggleFullscreen());
        });

        return container;
      },
    });

    new FullscreenControl().addTo(this.map);
  }

  private updateMapViewportHeight(): void {
    const mapElement = this.mapElementRef?.nativeElement;
    if (!mapElement) {
      return;
    }

    if (document.fullscreenElement?.contains(mapElement)) {
      mapElement.style.removeProperty('--tle-map-height');
      return;
    }

    const viewportPadding = window.innerWidth <= 768 ? 12 : 16;
    const minimumHeight = window.innerWidth <= 700 ? 430 : 320;
    const mapSpacing = Number.parseFloat(window.getComputedStyle(mapElement).marginBottom || '0') || 0;
    const availableHeight = Math.floor(
      window.innerHeight -
        mapElement.getBoundingClientRect().top -
        this.getFooterReservedHeight() -
        mapSpacing -
        viewportPadding
    );
    const nextHeight = Math.max(minimumHeight, availableHeight);

    mapElement.style.setProperty('--tle-map-height', `${nextHeight}px`);
    const wrapper = mapElement.closest('.tle-preview-wrapper') as HTMLElement | null;
    if (wrapper) {
      const sidebar = wrapper.querySelector('.location-sidebar');
      const panelHeight = nextHeight + mapElement.getBoundingClientRect().top - (sidebar?.getBoundingClientRect().top ?? wrapper.getBoundingClientRect().top);
      wrapper.style.setProperty('--tle-panel-height', `${panelHeight}px`);
    }
  }

  // The app footer is a `div.footer` and is fixed on some layouts, so reserve what it actually covers.
  private getFooterReservedHeight(): number {
    const footerElement = document.querySelector('.footer') as HTMLElement | null;
    if (!footerElement) {
      return 0;
    }

    const footerRect = footerElement.getBoundingClientRect();
    const footerStyles = window.getComputedStyle(footerElement);

    if (footerStyles.position === 'fixed' || footerStyles.position === 'sticky') {
      return Math.max(0, Math.ceil(window.innerHeight - footerRect.top));
    }

    const marginTop = Number.parseFloat(footerStyles.marginTop || '0') || 0;
    const marginBottom = Number.parseFloat(footerStyles.marginBottom || '0') || 0;
    return Math.ceil(footerRect.height + marginTop + marginBottom);
  }

  private async refreshCurrentUser(): Promise<void> {
    const user = await this.authService.setAndGetCurrentUser();
    if (user) {
      this.currentUser = user;
    }
  }

  private loadTleLocations(): void {
    this.locationsLoading = true;
    this.locationsError = '';
    this.tleLocationsSubscription?.unsubscribe();
    this.tleLocationsSubscription = this.locationCatalog.getLocations().subscribe({
      next: response => {
        this.parents = (response?.locations ?? []).map(parent => ({ ...parent, subLocations: parent.subLocations ?? [] }));
        this.visibleParents = filterTleLocations(this.parents, this.searchQuery);
        this.parents.flatMap(parent => parent.subLocations).forEach(location => this.getLocationColor(location.id));
        if (this.parents[0]) this.expandedIds.add(this.parents[0].id);
        this.locationsLoading = false;
      },
      error: () => {
        this.locationsLoading = false;
        this.locationsError = 'Unable to load locations. Please try again.';
      },
    });
  }

  private observeMapContainer(mapElement: HTMLElement): void {
    this.resizeObserver?.disconnect();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleMapResize();
    });
    this.resizeObserver.observe(mapElement);
  }

  private scheduleMapResize(): void {
    if (!this.map) {
      return;
    }

    requestAnimationFrame(() => {
      this.updateMapViewportHeight();
      this.map?.invalidateSize();
    });

    setTimeout(() => {
      this.updateMapViewportHeight();
      this.map?.invalidateSize();
    }, 150);
  }

  private buildSatelliteRequestPayload(
    context: LocationFetchContext,
    action: 'currentTLEPosition' | 'tlePreview',
    filteredSatellites: TlePreviewSensorPayloadEntry[] = []
  ): Record<string, unknown> {
    const locationName = context.locationName;
    const subLocationName = context.location.name;

    if (action === 'currentTLEPosition') {
      return {
        sensorData: {
          action,
          locationName,
          subLocationName,
          userId: context.userId,
        },
      };
    }

    return {
      sensorData: {
        action,
        locationName,
        subLocationName,
        userId: context.userId,
        sensors: filteredSatellites.map((satellite) => ({
          timeStamp: satellite.timestamp,
          noradId: satellite.noradId,
          sensor_ids: satellite.sensorIds,
          name: satellite.name,
          operatorKey: satellite.operatorKey,
        })),
      },
    };
  }

  private extractSatelliteResults(response: any): any[] {
    return this.tleHelperService.extractSatelliteResults(response);
  }

  private filterSatellitesByCurrentPosition(
    satellites: any[],
    location: Readonly<TleSubLocation>
  ): TlePreviewSensorPayloadEntry[] {
    const poi: L.LatLngTuple = [location.latitude, location.longitude];
    return satellites.flatMap((satellite: any) => {
      const sensorName = this.getSatelliteRequestName(satellite);
      const noradId = this.getSatelliteNoradId(satellite);
      const sensorIds = this.tleHelperService.getSatelliteSensorIds(satellite);
      const operatorKey = this.getSatelliteOperatorKey(satellite);
      const points = this.getSatelliteTrackPoints(satellite?.position);
      const swathWidthKm = this.getSatelliteSwathWidthKm(satellite);

      if (
        !sensorName ||
        !noradId ||
        !sensorIds.length ||
        !operatorKey ||
        !points.length ||
        swathWidthKm === null
      ) {
        return [];
      }

      return this.getQualifyingSatelliteTrackPoints(
        points,
        poi,
        swathWidthKm + TLE_PREVIEW_SWATH_BUFFER_KM
      )
        .slice(0, 1)
        .map((trackPoint) => {
          const timestamp = this.getSatellitePayloadTimestamp(
            trackPoint.timeStamp,
            satellite?.time
          );

          if (!timestamp) {
            return null;
          }

          return {
            timestamp,
            noradId,
            sensorIds,
            name: sensorName,
            operatorKey,
          };
        })
        .filter(
          (
            entry: TlePreviewSensorPayloadEntry | null
          ): entry is TlePreviewSensorPayloadEntry => !!entry
        );
    });
  }

  private getQualifyingSatelliteTrackPoints(
    trackPoints: SatelliteTrackEntry[],
    referencePoint: L.LatLngTuple,
    swathWidthKm: number
  ): SatelliteTrackEntry[] {
    return this.tleHelperService
      .getQualifyingSatelliteTrackPoints(
        trackPoints,
        referencePoint,
        swathWidthKm
      )
      .map(({ point, timeStamp }) => ({ point, timeStamp }));
  }

  private getSatelliteSensorName(satellite: any): string | null {
    const sensorName = satellite?.sensorName ?? satellite?.name ?? satellite?.key ?? satellite?.sensor?.name ?? null;
    return typeof sensorName === 'string' && sensorName.trim() ? sensorName.trim() : null;
  }

  private getSatelliteDisplayName(satellite: any): string | null {
    const displayName =
      this.currentUser?.obfuscated && satellite?.obfuscateName
        ? satellite.obfuscateName
        : satellite?.name ?? satellite?.sensorName ?? satellite?.key ?? satellite?.sensor?.name ?? null;

    return typeof displayName === 'string' && displayName.trim()
      ? displayName.trim()
      : null;
  }

  private getSatelliteRequestName(satellite: any): string | null {
    return this.getSatelliteSensorName(satellite);
  }

  private getSatelliteNoradId(satellite: any): string | null {
    return this.tleHelperService.getSatelliteNoradId(satellite);
  }

  private getSatelliteOperatorKey(satellite: any): string | null {
    return this.tleHelperService.getSatelliteOperatorKey(satellite);
  }

  private getSatellitePayloadTimestamp(timeStamp?: string, fallback?: string): string | null {
    return this.tleHelperService.getSatellitePayloadTimestamp(timeStamp, fallback);
  }

  private getSatelliteSwathWidthKm(satellite: any): number | null {
    const swath = satellite?.swath ?? null;

    if (swath === null || swath === undefined || swath === '') {
      return null;
    }

    const normalizedSwath = Number(
      typeof swath === 'string' ? swath.replace(/,/g, '') : swath
    );

    return Number.isFinite(normalizedSwath) && normalizedSwath > 0
      ? normalizedSwath
      : null;
  }

  private plotNearestPass(result: any, location: Readonly<TleSubLocation>): boolean {
    if (!this.map || !location || !this.selectedIds.has(location.id)) {
      return false;
    }

    const poi: L.LatLngTuple = [location.latitude, location.longitude];
    this.removeLocationPasses(location.id);

    this.ensureSatellitePane();
    const passGroup = L.layerGroup([], { pane: 'satellitePane' });
    let renderedPassCount = 0;

    if (Array.isArray(result)) {
      result.forEach((satellite: any) => {
        const points = this.getSatelliteTrackPoints(satellite?.position);
        if (!points.length) {
          return;
        }

        const validSegments: SatelliteTrackEntry[][] = [];
        let segment: SatelliteTrackEntry[] = [points[0]];

        for (let index = 1; index < points.length; index++) {
          const previousPoint = points[index - 1];
          const currentPoint = points[index];
          const lonDiff = Math.abs(currentPoint.point[1] - previousPoint.point[1]);
          const latDiff = Math.abs(currentPoint.point[0] - previousPoint.point[0]);

          if (lonDiff > 120 || latDiff > 60) {
            if (segment.length > 0) {
              validSegments.push(segment);
            }
            segment = [];
          }

          segment.push(currentPoint);
        }

        if (segment.length > 0) {
          validSegments.push(segment);
        }

        if (!validSegments.length) {
          return;
        }

        const fullPassPoints = validSegments.flat();
        const fullPassNearestPoint = this.getNearestSatelliteTrackPoint(
          fullPassPoints,
          poi
        );
        if (!fullPassNearestPoint) {
          return;
        }

        const passTimestamp = this.formatSatellitePassTimestamp(
          fullPassNearestPoint.timeStamp,
          satellite?.time
        );
        const satelliteName = this.getSatelliteDisplayName(satellite);
        const lineColor = this.getLocationColor(location.id);

        if (!satelliteName) {
          return;
        }

        validSegments
          .filter((passPoints) => passPoints.length > 1)
          .forEach((passPoints) => {
            const passLayer = L.polyline(
              passPoints.map((trackPoint) => trackPoint.point),
              {
                className: 'satellite-pass-line',
                color: lineColor,
                weight: 2.5,
                pane: 'satellitePane',
                interactive: true,
                renderer: this.satRenderer,
              }
            ).addTo(passGroup);
            this.bindSatellitePassHover(passLayer, passPoints);
          });

        const firstPassPoint = fullPassNearestPoint;
        const firstPassStart = firstPassPoint?.point;
        if (!firstPassStart) {
          return;
        }

        const time = this.formatSatellitePassTooltipTimestamp(
          firstPassPoint?.timeStamp,
          passTimestamp || satellite?.time
        );

        L.marker(firstPassStart, {
          icon: L.divIcon({
            className: 'tle-satellite-icon',
            html: `<span style="background:${lineColor}"><img src="assets/images/satellite.png" alt="" /></span>`,
            iconSize: [34, 34],
            iconAnchor: [17, 17],
          }),
          pane: 'satellitePane',
          interactive: false,
          zIndexOffset: 1000,
        }).addTo(passGroup);

        L.marker(firstPassStart, {
          icon: L.divIcon({
            className: 'satellite-label',
            html: `<div class="satellite-label-text"><div class="satellite-label-name" style="background:${lineColor}">${this.escapeHtml(satelliteName)}</div><div class="satellite-label-time">${this.escapeHtml(time)}</div></div>`,
            iconSize: [180, 52],
            iconAnchor: [-18, 26],
          }),
          pane: 'satellitePane',
          interactive: false,
        }).addTo(passGroup);

        renderedPassCount += 1;
      });
    }

    if (!renderedPassCount) {
      return false;
    }

    passGroup.addTo(this.map);
    this.locationPassLayers.set(location.id, passGroup);
    return true;
  }

  private escapeHtml(value: string): string {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  private removeLocationPasses(id: string): void {
    const layer = this.locationPassLayers.get(id);
    layer?.clearLayers();
    layer?.remove();
    this.locationPassLayers.delete(id);
  }

  private syncSatelliteOverlayPanes = (): void => {
    if (!this.map) {
      return;
    }

    const mapPane = this.map.getPane('mapPane');
    const satellitePassTooltipPane = this.map.getPane('satellitePassTooltipPane');

    if (!mapPane || !satellitePassTooltipPane) {
      return;
    }

    satellitePassTooltipPane.style.transform = mapPane.style.transform;
    satellitePassTooltipPane.style.webkitTransform = mapPane.style.webkitTransform;
    satellitePassTooltipPane.style.transformOrigin = mapPane.style.transformOrigin;
    satellitePassTooltipPane.style.webkitTransformOrigin = mapPane.style.webkitTransformOrigin;
  };

  private bindSatellitePaneSync(): void {
    if (!this.map || this.satellitePaneSyncBound) {
      return;
    }

    this.map.on('move', this.syncSatelliteOverlayPanes);
    this.map.on('zoom', this.syncSatelliteOverlayPanes);
    this.map.on('zoomanim', this.syncSatelliteOverlayPanes);
    this.map.on('viewreset', this.syncSatelliteOverlayPanes);
    this.map.on('resize', this.syncSatelliteOverlayPanes);

    this.satellitePaneSyncBound = true;
    this.syncSatelliteOverlayPanes();
  }

  private unbindSatellitePaneSync(): void {
    if (!this.map || !this.satellitePaneSyncBound) {
      return;
    }

    this.map.off('move', this.syncSatelliteOverlayPanes);
    this.map.off('zoom', this.syncSatelliteOverlayPanes);
    this.map.off('zoomanim', this.syncSatelliteOverlayPanes);
    this.map.off('viewreset', this.syncSatelliteOverlayPanes);
    this.map.off('resize', this.syncSatelliteOverlayPanes);

    this.satellitePaneSyncBound = false;
  }

  private ensureSatellitePane(): void {
    if (!this.map) {
      return;
    }

    if (!this.map.getPane('satellitePane')) {
      const pane = this.map.createPane('satellitePane');
      pane.style.zIndex = '650';
    }

    if (!this.map.getPane('satellitePassTooltipPane')) {
      const pane = this.map.createPane(
        'satellitePassTooltipPane',
        this.map.getContainer()
      );
      pane.style.zIndex = '1300';
      pane.style.pointerEvents = 'none';
    }

    if (!this.satRenderer) {
      this.satRenderer = L.svg({ pane: 'satellitePane' });
      this.satRenderer.addTo(this.map);
    }

    this.bindSatellitePaneSync();
  }

  private pickMapOverlayDirection(
    point: L.Point,
    margin = 120,
    inset: { left?: number; right?: number; top?: number; bottom?: number } = {
      right: 100,
      top: 60,
    }
  ): 'left' | 'right' | 'top' | 'bottom' {
    const map = this.map;
    if (!map) {
      return 'top';
    }

    const size = map.getSize();
    const leftMargin = (inset.left ?? 0) + margin;
    const rightMargin = (inset.right ?? 0) + margin;
    const topMargin = (inset.top ?? 0) + margin;
    const bottomMargin = (inset.bottom ?? 0) + margin;

    if (point.x >= size.x - rightMargin) {
      return 'left';
    }

    if (point.x <= leftMargin) {
      return 'right';
    }

    if (point.y <= topMargin) {
      return 'bottom';
    }

    if (point.y >= size.y - bottomMargin) {
      return 'top';
    }

    return 'top';
  }

  private bindSatellitePassHover(
    passLayer: L.Polyline,
    trackPoints: SatelliteTrackEntry[]
  ): void {
    const tooltip = L.tooltip({
      sticky: true,
      direction: 'top',
      opacity: 1,
      pane: 'satellitePassTooltipPane',
      className: 'satellite-pass-tooltip',
    });
    passLayer.bindTooltip(tooltip);
    const updateTooltip = (event: L.LeafletMouseEvent): void => {
      if (!this.map) {
        return;
      }

      const nearestPoint = this.getNearestSatelliteTrackPoint(
        trackPoints,
        [event.latlng.lat, event.latlng.lng]
      );
      const timestamp = nearestPoint?.timeStamp;
      const time = timestamp && moment.utc(timestamp).isValid()
        ? this.formatSatellitePassTooltipTimestamp(timestamp)
        : '';
      passLayer.setTooltipContent(time ? this.escapeHtml(time) : '');
      if (!time) {
        passLayer.closeTooltip();
        return;
      }
      const direction = this.getSatellitePassTooltipDirection(
        this.map.latLngToContainerPoint(event.latlng),
        tooltip.getElement()
      );
      tooltip.options.direction = direction;
      tooltip.options.offset = this.getTooltipOffset(direction);
      passLayer.openTooltip(event.latlng);
    };
    passLayer.on('mouseover', updateTooltip);
    passLayer.on('mousemove', updateTooltip);
  }

  private getSatellitePassTooltipDirection(
    point: L.Point,
    tooltipElement?: HTMLElement | null
  ): 'left' | 'right' | 'top' | 'bottom' {
    if (!this.map) {
      return 'top';
    }

    const size = this.map.getSize();
    const tooltipWidth = Math.max(tooltipElement?.offsetWidth ?? 0, 260);
    const tooltipHeight = Math.max(tooltipElement?.offsetHeight ?? 0, 96);
    const halfTooltipWidth = Math.ceil(tooltipWidth / 2);
    const halfTooltipHeight = Math.ceil(tooltipHeight / 2);
    const directions: Array<'left' | 'right' | 'top' | 'bottom'> = [];
    const addDirection = (
      direction: 'left' | 'right' | 'top' | 'bottom'
    ): void => {
      if (!directions.includes(direction)) {
        directions.push(direction);
      }
    };

    if (point.x <= halfTooltipWidth + 18) {
      addDirection('right');
    }

    if (point.x >= size.x - (halfTooltipWidth + 18)) {
      addDirection('left');
    }

    if (point.y <= tooltipHeight + 18) {
      addDirection('bottom');
    }

    if (point.y >= size.y - (tooltipHeight + 18)) {
      addDirection('top');
    }

    addDirection('top');
    addDirection('right');
    addDirection('left');
    addDirection('bottom');

    const fitsDirection = (
      direction: 'left' | 'right' | 'top' | 'bottom'
    ): boolean => {
      switch (direction) {
        case 'left':
          return (
            point.x - 18 - tooltipWidth >= 0 &&
            point.y - halfTooltipHeight >= 0 &&
            point.y + halfTooltipHeight <= size.y
          );
        case 'right':
          return (
            point.x + 18 + tooltipWidth <= size.x &&
            point.y - halfTooltipHeight >= 0 &&
            point.y + halfTooltipHeight <= size.y
          );
        case 'bottom':
          return (
            point.x - halfTooltipWidth >= 0 &&
            point.x + halfTooltipWidth <= size.x &&
            point.y + 18 + tooltipHeight <= size.y
          );
        default:
          return (
            point.x - halfTooltipWidth >= 0 &&
            point.x + halfTooltipWidth <= size.x &&
            point.y - 18 - tooltipHeight >= 0
          );
      }
    };

    return directions.find((direction) => fitsDirection(direction)) || 'top';
  }

  private clampLatLngInsideMap(
    latLng: L.LatLngExpression,
    padding: { x?: number; y?: number } = { x: 80, y: 80 }
  ): L.LatLng {
    const map = this.map;
    if (!map) {
      return L.latLng(latLng);
    }

    const point = map.latLngToContainerPoint(latLng);
    const size = map.getSize();
    const paddingX = padding.x ?? 80;
    const paddingY = padding.y ?? 80;

    point.x = Math.max(paddingX, Math.min(size.x - paddingX, point.x));
    point.y = Math.max(paddingY, Math.min(size.y - paddingY, point.y));

    return map.containerPointToLatLng(point);
  }

  private getTooltipOffset(
    direction: 'left' | 'right' | 'top' | 'bottom'
  ): L.Point {
    switch (direction) {
      case 'left':
        return L.point(-18, 0);
      case 'right':
        return L.point(18, 0);
      case 'bottom':
        return L.point(0, 18);
      default:
        return L.point(0, -18);
    }
  }

  private getSatelliteTrackPoints(
    positions: any
  ): SatelliteTrackEntry[] {
    return this.tleHelperService.getSatelliteTrackPoints(positions);
  }

  private getNearestSatelliteTrackPoint(
    trackPoints: SatelliteTrackEntry[],
    referencePoint: L.LatLngTuple
  ): SatelliteTrackEntry | null {
    return this.tleHelperService.getNearestSatelliteTrackPoint(
      trackPoints,
      referencePoint
    );
  }

  private getNearestDistanceToPassKm(trackPoints: SatelliteTrackEntry[], referencePoint: L.LatLngTuple): number {
    return this.tleHelperService.getNearestDistanceToPassKm(trackPoints, referencePoint);
  }

  private getDistanceFromPointToSegmentKm(
    point: L.LatLngTuple,
    segmentStart: L.LatLngTuple,
    segmentEnd: L.LatLngTuple
  ): number {
    return this.tleHelperService.getDistanceFromPointToSegmentKm(
      point,
      segmentStart,
      segmentEnd
    );
  }

  private formatSatellitePassTimestamp(timeStamp?: string, fallback?: string): string {
    return this.tleHelperService.formatSatellitePassTimestamp(timeStamp, fallback);
  }

  private formatSatellitePassTooltipTimestamp(timeStamp?: string, fallback?: string): string {
    return this.tleHelperService.formatSatellitePassTooltipTimestamp(
      timeStamp,
      fallback
    );
  }

  private normalizeLongitude(longitude: number): number {
    return ((((longitude + 180) % 360) + 360) % 360) - 180;
  }

  private normalizeLatLng(lat: number, lng: number): L.LatLng {
    const boundedLat = Math.max(-90, Math.min(90, lat));
    return L.latLng(boundedLat, lng).wrap();
  }

  private updateMapStats(): void {
    if (!this.map) {
      return;
    }

    this.zoomLevel = this.map.getZoom();
    const bounds = this.map.getBounds();
    const distance = bounds.getSouthWest().distanceTo(bounds.getSouthEast());
    this.mapDistance = distance / 1000;
  }

  private bindLegendMapPropagation(element: HTMLElement): void {
    if (this.legendPropagationElement === element) {
      return;
    }

    this.legendPropagationElement = element;
    L.DomEvent.disableClickPropagation(element);
    L.DomEvent.disableScrollPropagation(element);
  }

  private getDistanceKm(a: L.LatLngTuple, b: L.LatLngTuple): number {
    return this.tleHelperService.getDistanceKm(a, b);
  }

  private toRad(value: number): number {
    return (value * Math.PI) / 180;
  }

  private decimalToDMS(lat: number, lng: number): string {
    const latRef = lat >= 0 ? 'N' : 'S';
    const lngRef = lng >= 0 ? 'E' : 'W';

    const latAbs = Math.abs(lat);
    const lngAbs = Math.abs(lng);

    const latDeg = Math.floor(latAbs);
    const lngDeg = Math.floor(lngAbs);

    const latMinFloat = (latAbs - latDeg) * 60;
    const lngMinFloat = (lngAbs - lngDeg) * 60;

    const latMin = Math.floor(latMinFloat);
    const lngMin = Math.floor(lngMinFloat);

    const latSec = ((latMinFloat - latMin) * 60).toFixed(2);
    const lngSec = ((lngMinFloat - lngMin) * 60).toFixed(2);

    return `Lng: ${lngDeg}\u00B0 ${lngMin}' ${lngSec}\" ${lngRef}, Lat: ${latDeg}\u00B0 ${latMin}' ${latSec}\" ${latRef}`;
  }
}
